import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
} from "node-library/dist/colors";
import { Severity, TestHierarchyNodeType } from "node-library/dist/constants";
import { getFileDisplayName } from "node-library/dist/helpers/file_helper";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanySampleSpecificationHeaders,
	FrontendCompanySampleSpecificationRow,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendSampleSpecificationReadingInfo,
	FrontendUserInfo,
} from "node-library/dist/portal";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import { ButtonTextLoadingAnimation } from "../../animation/ButtonLoadingAnimation";
import { BlackButton, OutlinedButton } from "../../button";
import { CustomDropdown } from "../../inputs";
import { RedErrorIcon } from "../../toast/errorIcons";
import { useSnackbar } from "../../toast/showToastMessage";
import { GreenTickIcon } from "../../toast/successIcons";
import AddSpecColumnModal from "../addSpecColumnModal";
import AddSpecRowModal from "../addSpecRowModal";
import { DownloadIcon } from "../../svgIcons";
import { downloadSpecificationFile } from "../../../helpers/frontendFileOperationHelper";
import CommonAutoCompleteDropdown from "../../commonAutoCompleteDropdown";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	user: FrontendUserInfo;
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;

	testCategory: FrontendCompanyTestCategory;
	selectedMatrix: FrontendCompanySampleMatrix;
	updateSpecification: (spec: FrontendSampleSpecification) => void;

	currentFolderBreadcrumbs: Array<FrontendSampleSpecificationDirectory>;

	selectedSpecFile: FrontendSampleSpecification;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CreateSpecificationNoteModalContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		user,
		company,
		setCompany,
		testCategory,
		selectedMatrix,
		updateSpecification,
		currentFolderBreadcrumbs,
		selectedSpecFile,
		setUnsavedChanges,
	} = props;

	const doesUserHaveEditAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.specs.createReading,
			)
		: false;

	const doesUserHaveAdminAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.specs.createReadingRowCol,
			)
		: false;

	const testNodes: Array<FrontendCompanyTestHierarchyNode> =
		testCategory.hierarchyNodes
			.filter((node) => node.nodeType === TestHierarchyNodeType.TEST)
			.sort((a, b) => {
				// 1. Group by parentNodeId
				// Handle potential undefined values by treating them as empty strings
				const parentA = a.parentNodeId || "";
				const parentB = b.parentNodeId || "";

				if (parentA !== parentB) {
					return parentA.localeCompare(parentB);
				}

				// 2. Within the same parent, sort by nodeDisplayOrder
				return a.nodeDisplayOrder - b.nodeDisplayOrder;
			});

	const [selectedTestNode, setSelectedTestNode] = useState<
		FrontendCompanyTestHierarchyNode | undefined
	>(testNodes.length > 0 ? testNodes[0] : undefined);
	// Find the actual node object based on the state ID

	const [isSaving, setIsSaving] = useState(false);

	const [readingInfos, setReadingInfos] = useState<
		Array<FrontendSampleSpecificationReadingInfo>
	>([]);

	const [addColumnModalOpen, setAddColumnModalOpen] = useState<boolean>(false);
	const [addRowModalOpen, setAddRowModalOpen] = useState<boolean>(false);

	// When the component loads or selectedSpecFile changes, you would typically fetch or filter the data
	useEffect(() => {
		// Logic to populate readingInfos based on selectedSpecFile
		if (selectedSpecFile.readingFileId) {
			loadReadingFile();
		}
	}, [selectedSpecFile]);

	async function loadReadingFile() {
		// Optional: set loading state if you want a spinner over the table
		try {
			setIsSaving(true);

			const response = await sendGETRequest(
				"/spec-readings/get-specification-readings",
				{
					readingFileId: selectedSpecFile.readingFileId,
				},
			);

			if (response && response.success && response.data.readingInfo) {
				// Update the state with the readings from the backend
				setReadingInfos(response.data.readingInfo);
			} else {
				// If the request was successful but returned no data, clear the table
				setReadingInfos([]);
			}
		} catch (error) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
			setReadingInfos([]); // Clear data on error for safety
		} finally {
			setIsSaving(false);
		}
	}

	const handleUpdateReading = (
		rowId: string,
		headerId: string,
		newValue: string,
	) => {
		setReadingInfos((prev) => {
			const existingIndex = prev.findIndex(
				(r) => r.rowId === rowId && r.headerId === headerId,
			);

			if (existingIndex > -1) {
				// Update existing record
				const updated = [...prev];
				updated[existingIndex] = { ...updated[existingIndex], value: newValue };
				return updated;
			} else {
				// Create new record for this cell
				return [
					...prev,
					{
						readingId: "", // Backend will generate this
						fileId: selectedSpecFile.readingFileId as string,
						rowId: rowId,
						headerId: headerId,
						value: newValue,
					},
				];
			}
		});
		setUnsavedChanges(true);
	};

	const handleSaveReadings = async () => {
		if (readingInfos.length === 0) {
			openSnackbar(
				translation("NoData"),
				Severity.WARN,
				translation("PleaseEnterSomeData"),
			);
			return;
		}

		setIsSaving(true);
		try {
			// Map local state to the format the backend params expect
			const payload = {
				specificationId: selectedSpecFile.specificationId,
				readings: readingInfos.map((info) => ({
					headerId: info.headerId,
					rowId: info.rowId,
					value: info.value,
				})),
			};

			const response = await sendPOSTRequest(
				"/spec-readings/create-or-edit-spec-reading-file",
				payload,
			);

			if (response.success) {
				openSnackbar(
					translation("ReadingsUpdated"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />,
				);
				if (response.data.readingFileId && !selectedSpecFile.readingFileId) {
					updateSpecification({
						...selectedSpecFile,
						readingFileId: response.data.readingFileId,
					});
				}
				setUnsavedChanges(false);
			}
		} catch (error) {
			console.error("Save Error:", error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setIsSaving(false);
		}
	};

	const uploadToDirectoryString: string = [
		selectedMatrix.subMatrix,
		...currentFolderBreadcrumbs.map((folder) => {
			return folder.name;
		}),
	].join("/");

	const headerRow: React.ReactNode = (
		<div style={{ marginTop: "10px" }}>
			<div
				style={{
					padding: "8px",
					borderRadius: "6px",
					backgroundColor: SecondaryColor.SECONDARY_200,
				}}
			>
				<div
					className="typography-caption"
					style={{ color: NeutralColor.NEUTRAL_GREY_600 }}
				>
					{`${translation("UploadedSpecificationFile")}`}
				</div>
				<div style={{ display: "flex", gap: "5px" }}>
					{` ${uploadToDirectoryString}/${getFileDisplayName(
						selectedSpecFile.fileName,
						selectedSpecFile.format,
					)}`}
					{selectedSpecFile.canDownload && (
						<span
							style={{ cursor: "pointer" }}
							onClick={() => {
								downloadSpecificationFile(selectedSpecFile);
							}}
						>
							<DownloadIcon strokeColor="blue" />
						</span>
					)}
				</div>
			</div>
		</div>
	);

	const testNodeSelector: React.ReactNode = (
		<CommonAutoCompleteDropdown
			id={"select-test-dropdown"}
			label={translation("SelectTest")}
			options={testNodes}
			value={selectedTestNode}
			setValue={(testNode: FrontendCompanyTestHierarchyNode) => {
				setSelectedTestNode(testNode);
			}}
			getOptionLabel={(testNode: FrontendCompanyTestHierarchyNode) => {
				return testNode.nodeName;
			}}
		/>
	);

	const buttonPanel: React.ReactNode = (
		<div className="spec-reading-selector-header-button-panel">
			{
				<OutlinedButton
					id={"profile-create-folder-button"}
					size="small"
					buttonClassName="button-with-left-icon"
					onClick={() => {
						if (selectedSpecFile.readingFileId) {
							loadReadingFile();
						} else {
							setReadingInfos([]);
						}
						setUnsavedChanges(false);
					}}
					disabled={isSaving}
					outlineColor={""}
				>
					{isSaving ? (
						<ButtonTextLoadingAnimation text={translation("Loading")} />
					) : (
						<span
						// className="new-order-collapsible-text"
						>
							{translation("Revert")}
						</span>
					)}
				</OutlinedButton>
			}
			{
				<BlackButton
					id={"save-reading-button"}
					size="small"
					onClick={handleSaveReadings}
					disabled={isSaving}
				>
					{isSaving ? (
						<ButtonTextLoadingAnimation text={translation("Loading")} />
					) : (
						<span>{translation("Save")}</span>
					)}
				</BlackButton>
			}
		</div>
	);
	const readingHeaderRow: React.ReactNode = (
		<div className="spec-reading-selector-header-row">
			{testNodeSelector}
			{doesUserHaveEditAccess ? buttonPanel : <></>}
		</div>
	);
	const headers: Array<FrontendCompanySampleSpecificationHeaders> =
		company.companySpecHeaders;
	const rows: Array<FrontendCompanySampleSpecificationRow> =
		company.companySpecRows.filter((row) => {
			return (
				row.testCategoryId === testCategory.testCategoryId &&
				row.hierarchyNodeId === selectedTestNode?.nodeId
			);
		});

	const editableTable: React.ReactNode = (
		<TableContainer
			component={Paper}
			sx={{
				borderRadius: "12px",
				border: `1px solid ${NeutralColor.NEUTRAL_GREY_300}`,
				boxShadow: "none",
				overflow: "hidden",
				margin: "10px 0px",
			}}
		>
			<Table>
				<TableHead sx={{ backgroundColor: "#F9FAFB" }}>
					<TableRow>
						<TableCell sx={{ fontWeight: "bold" }}>
							{translation("Title")}
						</TableCell>
						{/* Map Columns */}
						{headers
							.sort((a, b) => a.displayOrder - b.displayOrder)
							.map((header) => (
								<TableCell key={header.headerId} sx={{ fontWeight: "bold" }}>
									{header.name}
								</TableCell>
							))}
						{/* Hide Column Button if no access */}
						{doesUserHaveAdminAccess && (
							<TableCell sx={{ width: "50px" }}>
								<OutlinedButton
									id="add-column-button"
									size="small"
									onClick={() => setAddColumnModalOpen(true)}
									outlineColor={PrimaryColor.PRIMARY_500}
								>
									+ {translation("Column")}
								</OutlinedButton>
							</TableCell>
						)}
					</TableRow>
				</TableHead>
				<TableBody>
					{/* Map Rows */}
					{rows
						.sort((a, b) => a.displayOrder - b.displayOrder)
						.map((row) => (
							<TableRow key={row.rowId}>
								<TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>

								{/* Map Editable Cells */}
								{headers.map((header) => {
									const currentReading = readingInfos.find(
										(r) =>
											r.rowId === row.rowId && r.headerId === header.headerId,
									);

									return (
										<TableCell
											key={`${row.rowId}-${header.headerId}`}
											sx={{ padding: "4px 16px" }}
										>
											<TextField
												fullWidth
												variant="standard"
												value={currentReading?.value || ""}
												// Add the disabled prop here
												disabled={!doesUserHaveEditAccess}
												onChange={(e) =>
													handleUpdateReading(
														row.rowId,
														header.headerId,
														e.target.value,
													)
												}
												InputProps={{
													disableUnderline: true,
													sx: { fontSize: "14px", fontFamily: "Lexend" },
												}}
												placeholder="—"
											/>
										</TableCell>
									);
								})}
							</TableRow>
						))}
					{/* Add Row Button at the bottom - only show if user has access */}
					{doesUserHaveAdminAccess && (
						<TableRow>
							<TableCell colSpan={headers.length + 2}>
								<OutlinedButton
									id="add-row-button"
									size="small"
									onClick={() => setAddRowModalOpen(true)}
									buttonClassName="button-with-left-icon"
									outlineColor={PrimaryColor.PRIMARY_500}
								>
									+ {translation("AddRow")}
								</OutlinedButton>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);

	return (
		<div>
			{headerRow}
			<div>
				{readingHeaderRow}

				{editableTable}
			</div>
			<AddSpecColumnModal
				open={addColumnModalOpen}
				setOpen={setAddColumnModalOpen}
				company={company}
				setCompany={setCompany}
			></AddSpecColumnModal>
			<AddSpecRowModal
				open={addRowModalOpen}
				setOpen={setAddRowModalOpen}
				company={company}
				setCompany={setCompany}
				selectedTestNode={selectedTestNode!}
			></AddSpecRowModal>
		</div>
	);
}
